import {
    SmoothScroll, Timeout, isIOS, scrollToBottom, smoothScrollToBottom,
    smoothScrollToTarget
} from "../util/Misc";
import BubbleType from "../model/BubbleType";
import { Constants, UI_COMPONENT } from "../model/Constants";
import CustomScrollBar from "../util/CustomScrollBar";
import { createButton, deleteElement } from "../controller/TranscriptController";
import { isBubbleTypeRichMediaWidget } from "./bubbleviews/controller/BubbleController";
import pubSubGlobal from "../util/pubSubGlobal";
import { log } from "../chatframe/Utils";
import MultipleMessagesScrollHandler from "../util/MultipleMessagesScrollHandler";
import { transcriptRender } from "./templates/renders/transcript";

/**
 * Creates the transcript-area custom element which will the container for all the child bubble elements
 * @type {{template, expandArea(): void, created(): void, deleteMessageBubbles(*): void, reloadMessageBubbles(*, *=): void, scrollBottom(): void, tagName: string, checkIfMoreBubblesNeeded(): void, scrollUp(*): void, restoreScrollHandler(*=): void, removed(): void, renderMessageBubbles(*=, *=): (undefined), reset(), createView(*): *, renderMessageBubbleToTop(*=, *=): void, hideTyping(): void}}
 */
const Transcript: any = {
    tagName: "transcript-area",
    renderer: transcriptRender,
    initialize(options) {
        initComponent(options);
    },
    created() {
        this.tranCont = this.el.firstChild;
        this.footer = this.tranCont.lastElementChild;
        this.scollIn = new CustomScrollBar();
        this.tArea = this.scollIn.init(this.tranCont);
        this.footer = this.tranCont.lastElementChild;
        this.typingView = this.tArea.querySelector(UI_COMPONENT.TYPING_VIEW);
        this.scrollHandler = this.scrollUp.bind(this);
        this.multipleMessagesScrollHandler = new MultipleMessagesScrollHandler(this.enhancedScrollingDelay);
        this.onAgentBubbleSubscriptionId = this.multipleMessagesScrollHandler.subscribeToAgentBubbleCreation();

        if (!isIOS() || this.iosSmoothScroll) {
            this.smoothScroll = new SmoothScroll(this.tArea, isIOS() ? this.iosScrollSpeed : this.mouseScrollSpeed, 12);
        }

        this.newMsgBtn = null;

        if (this.bubbleFromBottom) {
            this.tArea.classList.add("bubble-bottom");
        }

        this.tArea.setAttribute("aria-label", this.$app.ariaStrings.transcriptLabel);
        this.lastScroll = 0;
        this.oldScrollHeight = 0;

        this.onKeyboardOpenSubscriptionId = pubSubGlobal.subscribe(() => {
            this.scrollBottom();
        }, Constants.VIRTUAL_KEYBOARD_DISPLAYED);

        this.onWindowResizedSubscriptionId = pubSubGlobal.subscribe(() => {
            this.scrollBottom();
        }, Constants.WINDOW_RESIZED);

        this.onChatWindowRestoredId = pubSubGlobal.subscribe(() => {
            this.scrollBottom();
        }, Constants.CHAT_WINDOW_RESTORED);
    },

    /**
     * expand the transcript area when user focus the customer input
     */
    expandArea() {
        window.raf(() => {
            this.tranCont.classList.remove("transcript-area-material");
        });
    },
    clearScrollTimer() {
        if (Timeout.exists("scrollTimer") && !Timeout.executed("scrollTimer")) {
            Timeout.clear("scrollTimer");
        }
    },

    /**
     * render the child  agent, customer, system bubbles
     * @param {Message} message - message to be rendered
     * @param {boolean} samePreviousType - add message to previous view
     */
    renderMessageBubbles(message, samePreviousType) {
        let view;
        this.lastMessage = message;

        /*
         * we always insert last bubble before typing-view component i.e. this.tArea.insertBefore(view, this.typingView);
         * so we can use previousElementSibling of typing-view component to get the lastView
         */
        this.lastView = this.typingView.previousElementSibling;
        const allowScrollToBottom = this.isScrollableToBottom();

        // Any occurence of below messages will reset the multiple messages queue and timer for scroll handling
        if ([BubbleType.CUSTOMER_MESSAGE, BubbleType.SYSTEM_MESSAGE].includes(message.getMessageBubble())) {
            this.multipleMessagesScrollHandler.resetQueueAndTimer();
        }

        if (samePreviousType && message.getMessageBubble() === this.getPreviousMessageType()) {
            view = this.lastView;
            this.lastView.addBubble(message);
        } else {
            if (message.getMessageBubble() === BubbleType.TYPING_MESSAGE) {
                this.typingView.setVisible(true, message);

                if (!this.shouldDisplayNewMessage(message) && allowScrollToBottom) {
                    this.clearScrollTimer();
                    this.smoothScrollToBottomEnhanced();
                }
                return;
            }

            view = this.createView(message);
            this.tArea.insertBefore(view, this.typingView);
            view.render(message);
        }

        this.lastView = view;
        const isRestored = message.getIsRestore();

        if (!isRestored && this.shouldDisplayNewMessage(message)) {
            this.createNewMsgButton();
            this.newMsgBtn.classList.add("enter");
        } else {
            this.handleScrollOnNewMessage(isRestored, allowScrollToBottom);
        }
    },

    /**
     * handle scroll on new message arrival
     * @param {boolean} isRestored
     */
    handleScrollOnNewMessage(isRestored, allowScrollToBottom) {
        if (!isRestored) {
            if (this.lastMessage.getMessageBubble() === BubbleType.CUSTOMER_MESSAGE || this.scrollToBottomOnAgentMessage || allowScrollToBottom) {
                this.clearScrollTimer();
                Timeout.set("scrollTimer", () => {
                    this.smoothScrollToBottomEnhanced();
                }, 100);
            } else {
                this.displayButtonImmediately = true;
                this.processDisplayGoToButton();
            }
        } else {
            this.restoreScrollHandler(() => {
                this.restoreTimer.clear(true);

                if (this.$app.getIsMinimized()) {
                    this.scrollBottom();
                } else {
                    this.smoothScrollToBottomEnhanced();
                }
            });
        }
        if (!this.scrollListenerEnabled) {
            this.scrollListener();
        }
    },

    /**
     * Smooth scroll this transcriptArea to the bottom
     * or to the top of the last message if enhancedScrollingEnabled is true.
     */
    smoothScrollToBottomEnhanced() {
        if (this.bottomScrollBtn && this.scrollBtnDisplayed) {
            const type = this.lastMessage.getMessageBubble();

            if (type === BubbleType.TYPING_MESSAGE) {
                return;
            }
        }

        if (this.smScrollInst) {
            this.smScrollInst.interrupt();
        }

        const target = this.getLastViewEnhancedScrollingTarget();

        this.performSmoothScroll(target);
    },

    /**
     * Scroll to bottom w.r.t target element or chat area
     *
     * @param {HTMLElement} target - scroll to element
     */
    performSmoothScroll(target) {
        if (target) {
            this.smScrollInst = smoothScrollToTarget(this.tArea, target, this.s2bDuration);
        } else {
            this.smScrollInst = smoothScrollToBottom(this.tArea, this.s2bDuration);
        }

        this.smScrollInst.p.then(() => {
            this.smScrollInst = null;
        }).catch(reason => {
            log(reason);
        });
    },

    /**
     * Gets the scroll target of the last rendered view if
     * enhancedScrollingEnabled is true, the last message was from agent and
     * message is bigger than the transcript area.
     *
     * @returns {number|undefined} - returns undefined when there is no target
     */
    getLastViewEnhancedScrollingTarget() {
        let scrollTarget;
        const type = this.lastMessage.getMessageBubble();

        const isRichMessage = isBubbleTypeRichMediaWidget(type);

        const isIncomingMessage = (
            type === BubbleType.AGENT_MESSAGE
            || type === BubbleType.VIRTUAL_AGENT_MESSAGE
            || type === BubbleType.OPENER_MESSAGE
            || isRichMessage
        );

        if (this.enhancedScrollingEnabled && isIncomingMessage) {
            scrollTarget = this.multipleMessagesScrollHandler.getInboundMessageHead(this.tArea.clientHeight);

            if (!scrollTarget) {
                const containerEl = this.lastView.getContainer();
                let scrollToEl = isRichMessage ? containerEl : containerEl.lastElementChild;

                if (scrollToEl.clientHeight > this.tArea.clientHeight) {
                    scrollTarget = scrollToEl.offsetTop + this.tArea.clientHeight;
                }
            }
        }
        return scrollTarget;
    },

    /**
     * Insert the child bubbles to the top of the transcript area, this is for easier handling of lazy loading or dynamic rendering of previous conversations
     * @param {Message} message - message to be rendered
     * @param {boolean} samePreviousType - add message to previous view
     */
    renderMessageBubbleToTop(message, samePreviousType) {
        let view;

        if (samePreviousType) {
            view = this.prevView || this.tArea.firstElementChild;
            view.insertBubble(message);
        } else {
            view = this.createView(message);
            this.tArea.insertBefore(view, this.tArea.firstElementChild);
            view.render(message);
        }

        /**
         * retaining scroll position after after rendering each bubble
         * This is to avoid flickering
         * @type {number}
         */
        const newScrollHeight = this.tArea.scrollHeight - this.tArea.clientHeight;
        this.tArea.scrollTop = this.lastScroll + (newScrollHeight - this.oldScrollHeight);

        if (this.smoothScroll) {
            this.smoothScroll.pause();
        }

        this.restoreScrollHandler(() => {
            this.checkIfMoreBubblesNeeded();
            this.scollIn.reinit();
            this.restoreTimer.clear(true);

            if (this.smoothScroll) {
                this.smoothScroll.resume();
            }

            if (!this.scrollListenerEnabled) {
                this.scrollListener();
            }
        });

        this.prevView = view;
    },

    /**
     * Timeout logic to handle the scrolling to bottom when restoring
     * @param {function} cb
     */
    restoreScrollHandler(cb) {
        if (this.restoreTimer && this.restoreTimer.pending()) {
            this.restoreTimer.restart();
        } else {
            if (this.restoreTimer) {
                this.restoreTimer.clear(true);
            }

            this.restoreTimer = Timeout.instantiate(cb, 250);
        }
    },

    /**
     * check if the visible area of chat window can have any more child bubbles, if yes then do a lazy loading which will inject
     * more conversation to the top of the chat window
     */
    checkIfMoreBubblesNeeded() {
        this.$app.lazyloading = false;
        let addH = this.tArea.clientHeight;

        if (this.topBarConfig && this.topBarConfig.topBarType === "material") {
            addH = addH + this.$app.shoutui.getTopBarHeight();
        } else {
            // adding a threshold for calculating if another conversation need to be rendered
            addH += 100;
        }

        // if scroll height is less than client height then it means more space to display bubbles
        if (this.tArea.scrollHeight < addH) {
            this.$app.lazyloadConversation();
        }
    },

    /**
     * delete the bubbles or hide the bubbles
     * @param {Array.<Message>} messages
     */
    deleteMessageBubbles(messages) {
        messages.forEach(message => {
            if (message.getMessageBubble() === BubbleType.TYPING_MESSAGE) {
                this.typingView.setVisible(false);
            } else if (message.getMessageBubble() === BubbleType.SYSTEM_MESSAGE) {
                const tmpView = this.lastView;
                this.lastView = this.tempLastView;
                this.tempLastView = null;
                deleteElement(message.getIsRestore(), tmpView);
            } else if (message.getRichData() != null) {
                const tmpView = this.lastView;
                this.lastView = this.tempLastView;
                this.tempLastView = null;
                deleteElement(true, tmpView);
            }
        });
    },

    /**
     * reload the child bubble element
     * @param {number} index
     * @param {Message} message
     */
    reloadMessageBubbles(index, message) {
        if (message.getMessageBubble() === BubbleType.TYPING_MESSAGE) {
            this.typingView.reload(message.getMessageText());
        } else if (message.getMessageBubble() === BubbleType.SYSTEM_MESSAGE) {
            this.lastView.render(message);
        }

        if (message.getRichData() != null) {
            this.lastView.reload(message);
        }
    },

    /**
     * create the child bubble element depends on the BubbleType
     * @param {Message} message - message to create a view for
     * @return {HTMLElement} - Created view based on message bubble type
     */
    createView(message) {
        let view;
        const type = message.getMessageBubble();

        if (isBubbleTypeRichMediaWidget(type)) {
            view = document.createElement("rich-view");
            this.tempLastView = this.lastView;
        } else {
            switch (type) {
                case BubbleType.AGENT_MESSAGE:

                // falls through
                case BubbleType.VIRTUAL_AGENT_MESSAGE:

                // falls through
                case BubbleType.OPENER_MESSAGE:
                    view = document.createElement(UI_COMPONENT.AGENT_SIMPLE_VIEW);
                    break;

                case BubbleType.CUSTOMER_MESSAGE:
                    view = document.createElement(UI_COMPONENT.CUSTOMER_SIMPLE_VIEW);
                    break;

                case BubbleType.CONVERSATION_HEADER:
                    view = document.createElement(UI_COMPONENT.HEADER_VIEW);
                    break;

                case BubbleType.DISCLAIMER_TEXT:
                    view = document.createElement(UI_COMPONENT.DISCLAIMER_VIEW);
                    break;

                case BubbleType.SYSTEM_MESSAGE:
                    view = document.createElement(UI_COMPONENT.SYSTEM_VIEW);
                    this.tempLastView = this.lastView;
                    break;

                default:
                    throw new Error(`Unknown bubble type: "${type}" given to createView`);
            }
        }
        return view;
    },
    removed() {
        this.scollIn.clear();
        this.tArea.removeEventListener("scroll", this.scrollHandler);

        if (this.smoothScroll) {
            this.smoothScroll.pause();
        }

        pubSubGlobal.unsubscribe(this.onKeyboardOpenSubscriptionId);
        pubSubGlobal.unsubscribe(this.onWindowResizedSubscriptionId);
        pubSubGlobal.unsubscribe(this.onAgentBubbleSubscriptionId);
    },

    /**
     * Force the transcript area scrolling to bottom
     */
    scrollBottom() {
        window.raf(() => {
            scrollToBottom(this.tArea);
        });
    },

    /**
     * Listens to scroll event to handle the lazy loading when
     * the scroll reaches the top
     * @param {Event} el - element
     */
    scrollUp(el) {
        const tar = el.target;
        this.oldScrollHeight = tar.scrollHeight - tar.clientHeight;

        if (this.$app.hasMoreConversations()
            && !this.$app.getRestoring()
            && tar.scrollTop < 15
        ) {
            el.target.removeEventListener("scroll", this.scrollHandler);
            this.scrollListenerEnabled = false;
            this.lastScroll = tar.scrollTop;

            if (this.smoothScroll) {
                this.smoothScroll.pause();
            }

            this.$app.lazyloadConversation();
            return;
        }

        /**
         * logic to control the display of scroll to bottom button,
         * if the user scrolled transcript display scroll down button.
         * Also if user manually scrolls down through mouse scroll,
         * remove the scroll to bottom button if user has reached bottom
         */
        this.processDisplayGoToButton();

        this.lastScroll = tar.scrollTop;
    },

    /**
     * Process the displying hiding of scroll to bottom button
     */
    processDisplayGoToButton() {
        if (this.bottomScrollBtn && !this.$app.getRestoring()) {
            const clientHeight = this.tArea.clientHeight;
            const scrollPos = this.tArea.scrollTop;

            if (!this.scrollBtnDisplayed) {
                // should be displayed for new messages which are longer than chat window
                const bottomLimit = (this.tArea.scrollHeight - (this.bottomScrollVal || clientHeight));
                const reachedThreshold = scrollPos + clientHeight < bottomLimit;

                if (reachedThreshold) {
                    this.displayGoToBottomButton();
                }
            }

            this.hideScrollButtonWhenAtBottom();
        }
    },

    hideScrollButtonWhenAtBottom() {
        if (this.scrollBtnDisplayed) {
            const almostBottom = ((this.tArea.scrollHeight - this.tArea.clientHeight - this.tArea.scrollTop) < 50);

            // When chat window is still rendering (i.e. this.tArea.clientHeight < 100) then hide scrollToBottomButton
            if (almostBottom || this.tArea.clientHeight < 100) {
                this.hideScrollButton();
            }
        }
    },

    isUserScrolledUp() {
        const ch = this.tArea.clientHeight;
        return (this.lastScroll + ch < (this.tArea.scrollHeight - ch))
    },

    /**
     * If new agent message comes and min required scrollable height is there then return true
     * @return {boolean}
     */
    isScrollableToBottom() {
        return this.tArea.scrollHeight - this.tArea.clientHeight - this.tArea.scrollTop < this.scrollToBottomOffSet;
    },

    /**
     * If async chat and async mode is all then show don't scroll to bottom
     * when there is an agent message and user has scrolled up
     * @param {Message} message
     * @return {boolean}
     */
    shouldDisplayNewMessage(message) {

        const mType = message.getMessageBubble();

        // least user has scrolled the size of the client height
        if (this.$app.isHeaderNeeded() && this.newMsgButton && this.isUserScrolledUp()) {
            if (mType === BubbleType.AGENT_MESSAGE || mType === BubbleType.OPENER_MESSAGE
                || mType === BubbleType.TYPING_MESSAGE || mType == BubbleType.VIRTUAL_AGENT_MESSAGE
                || mType == BubbleType.RICH_MESSAGE_FORM || mType == BubbleType.RICH_MESSAGE_FORM
                || mType == BubbleType.RICH_MESSAGE_QR
                || mType == BubbleType.RICH_MESSAGE_CARD || mType == BubbleType.RICH_MESSAGE_OTHER
            ) {
                return true;
            }
        }
        return false;
    },
    createNewMsgButton() {
        /**
         * if show a new message button display turned on, this get higher priority and hides scroll to bottom button
         */
        this.hideScrollButton();

        if (!this.newMsgBtn) {
            this.newMsgBtn = createButton("new-msg-button",
                this.newMsgBtnText, this.newImgStyle, () => {
                    this.smoothScrollToBottomEnhanced();
                    this.newMsgBtn.classList.remove("enter");
                });
            this.tranCont.appendChild(this.newMsgBtn);
        }
    },

    /**
     * creates a button that will be used to allow user to scroll easly to bottom
     */
    createScrollButton() {
        if (!this.scrollBtn) {
            this.scrollBtn = createButton("scroll-button",
                this.scrollBtnText, null, () => {
                    smoothScrollToBottom(this.tArea, this.s2bDuration);
                    this.hideScrollButton();
                });
            this.tranCont.appendChild(this.scrollBtn);
        }
    },
    hideTyping() {
        this.typingView.setVisible(false);
    },
    getTranscriptArea() {
        return this.tArea;
    },
    adjustBottom(val) {
        let oval = parseInt(this.tranCont.style.bottom || 0, 10);

        if (!oval) {
            oval = parseInt(window.getComputedStyle(this.tranCont).bottom, 10);
            this.tranCont.style.bottom = `${oval}px`;
        }

        if (oval > val) {
            this.tranCont.style.bottom = `${val}px`;
        } else if (val > oval) {
            this.tranCont.style.bottom = `${val}px`;
            scrollToBottom(this.tArea);
        }
    },

    /**
     * hide goToBottom Button and also clear if there is any timer scheduled to show it
     */
    hideScrollButton() {
        Timeout.clear(Constants.SCROLL_BTN_TIMER_NAME);

        if (this.scrollBtn) {
            this.scrollBtn.classList.remove("enter");
        }

        this.scrollBtnDisplayed = false;
    },

    /**
     * register scroll Listener on Transcript Area
     */
    scrollListener() {
        this.tArea.addEventListener("scroll", this.scrollHandler);
        this.scrollListenerEnabled = true;
    },

    /**
     * Schedule a timer to display GoTo Bottom BUtton
     */
    displayGoToBottomButton() {
        const createScrollBtn = () => {
            this.createScrollButton();
            this.scrollBtn.classList.add("enter");
            this.scrollBtnDisplayed = true;
            this.hideScrollButtonWhenAtBottom();
        };

        if (this.displayButtonImmediately) {
            createScrollBtn();
            this.displayButtonImmediately = false;
        } else {
            Timeout.set(Constants.SCROLL_BTN_TIMER_NAME, () => {
                createScrollBtn();
            }, this.bottomScrollDelay);
        }
    },

    /**
     * Display the loading message
     * @param {Message} message
     * @param {boolean} state
     */
    toggleLoadingMessage(message, state) {
        if (!this.loadingView) {
            this.loadingView = this.createView(message);
            this.tranCont.appendChild(this.loadingView);
            this.loadingView.render(message);
        }

        if (state) {
            this.loadingView.show();
        } else {
            this.loadingView.hide();
        }
    },
    getPreviousMessageType() {
        const previousMessageContainer = this.lastView?.getContainer?.();
        const messageContainerChild = previousMessageContainer?.lastChild;
        return messageContainerChild?.getType() as number;
    },
};

/**
 * @export Transcript
 * @param {CIApp} appInstance
 * @return {{template, expandArea, (): void, created, (): void, deleteMessageBubbles, (*): void, reloadMessageBubbles, (*, *=): void, scrollBottom, (): void, tagName: string, checkIfMoreBubblesNeeded, (): void, scrollUp, (*): void, restoreScrollHandler, (*=): void, removed, (): void, renderMessageBubbles, (*=, *=): undefined, reset, (), createView, (*): *, renderMessageBubbleToTop, (*=, *=): void, hideTyping, (): void}}
 */
export default appInstance => {
    Transcript.$app = appInstance;
    const constants: any = {};
    Transcript.constants = constants;
    return Transcript;
};

const initComponent = (options: any) => {
    const app = options.$app;
    const constants = options.constants;
    const config = app.getUIConfig();

    if (config.transcriptConfig) {
        constants.newMsgBtnText = config.transcriptConfig.newMsgButtonText || "New Message";
        constants.scrollBtnText = config.transcriptConfig.scrollToBottomButtonText || "Scroll To Bottom";
    }

    if (config.transcriptConfig) {
        constants.style = config.transcriptConfig.transcriptAreaStyles;
        constants.newImgStyle = config.transcriptConfig.newMessageButtonStyles;
    }

    if (config.topBarConfig) {
        constants.topBarConfig = config.topBarConfig.topBarSettings || {};
    }

    if (config.footerConfig) {
        constants.footerSettings = config.footerConfig.footerSettings || {};
    }

    const appConfig = config.appConfigProps;

    if (appConfig) {
        /**
         * Time it take for scroll to reach bottom
         * @type {number} s2bDuration - scroll2bottomDuration
         */
        constants.s2bDuration = appConfig.scrollDuration ?? 100;
        constants.bubbleFromBottom = appConfig.setBubbleFromBottom || false;
        constants.newMsgButton = appConfig.showNewMessageButton || false;
        constants.footerDis = appConfig.showFooterDisclaimer;
        constants.iosSmoothScroll = appConfig.IOSSmoothScroll || false;
        constants.iosScrollSpeed = appConfig.IOSSmoothScrollSpeed ?? 15;
        constants.mouseScrollSpeed = appConfig.MouseScrollSpeed ?? 50;
        constants.bottomScrollBtn = appConfig.goToBottomButton || appConfig.scrollToBottomButton || false;
        constants.bottomScrollDelay = appConfig.goToBottomButtonDelay ?? appConfig.scrollToBottomDelay ?? 1000;
        constants.bottomScrollVal = appConfig.goToBottomThreshold;
        constants.enhancedScrollingEnabled = appConfig.enhancedScrollingEnabled || false;
        constants.enhancedScrollingDelay = appConfig.enhancedScrollingDelay || 750;
        constants.scrollToBottomOnAgentMessage = appConfig.scrollToBottomOnAgentMessage ?? true;
        constants.scrollToBottomOffSet = appConfig.scrollToBottomOffSet ?? 200;
    }
};
